import type {
    AvailableGateways,
    CardPaymentIntent,
    CreatePaymentIntentDTO,
    IPaymentGatewayRepository,
    PaymentIntent,
    ProcessRefundDTO,
    Refund,
} from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiAvailableGatewaysResponse {
  gateways: string[];
  hasConfigured: boolean;
}

interface ApiPaymentIntentResponse {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled';
  clientSecret?: string;
  paymentUrl?: string;
  metadata?: Record<string, unknown>;
}

interface ApiRefundResponse {
  id: string;
  status: 'pending' | 'succeeded' | 'failed';
  amount: number;
}

// Mapea la respuesta de la API al tipo AvailableGateways interno
const mapApiGatewaysToGateways = (apiGateways: ApiAvailableGatewaysResponse): AvailableGateways => ({
  gateways: apiGateways.gateways as AvailableGateways['gateways'],
  hasConfigured: apiGateways.hasConfigured,
});

// Mapea la respuesta de la API al tipo PaymentIntent interno
const mapApiPaymentIntentToPaymentIntent = (apiIntent: ApiPaymentIntentResponse): PaymentIntent => ({
  id: apiIntent.id,
  amount: apiIntent.amount,
  currency: apiIntent.currency,
  status: apiIntent.status,
  clientSecret: apiIntent.clientSecret,
  paymentUrl: apiIntent.paymentUrl,
  metadata: apiIntent.metadata,
});

// Mapea la respuesta de la API al tipo Refund interno
const mapApiRefundToRefund = (apiRefund: ApiRefundResponse): Refund => ({
  id: apiRefund.id,
  status: apiRefund.status,
  amount: apiRefund.amount,
});

export class ApiPaymentGatewayRepository implements IPaymentGatewayRepository {
  async getAvailableGateways(): Promise<AvailableGateways> {
    const client = getApiClient();
    const response = await client.get<ApiAvailableGatewaysResponse>('v1/payments/gateway/available');
    return mapApiGatewaysToGateways(response);
  }

  /**
   * Solo se manda la seleccion de cargos. Ni monto, ni moneda, ni descripcion: todo eso
   * lo decide el backend desde la base, y rechaza propiedades desconocidas.
   */
  async createPaymentIntent(intent: CreatePaymentIntentDTO): Promise<CardPaymentIntent> {
    const client = getApiClient();
    return client.post<CardPaymentIntent>('v1/payments/gateway/create-intent', {
      chargeIds: intent.chargeIds,
    });
  }

  async getPaymentIntent(gateway: string, id: string): Promise<PaymentIntent> {
    const client = getApiClient();
    const apiIntent = await client.get<ApiPaymentIntentResponse>(
      `v1/payments/gateway/intent/${gateway}/${id}`
    );
    return mapApiPaymentIntentToPaymentIntent(apiIntent);
  }

  async cancelPaymentIntent(gateway: string, id: string): Promise<PaymentIntent> {
    const client = getApiClient();
    const apiIntent = await client.post<ApiPaymentIntentResponse>(
      `v1/payments/gateway/intent/${gateway}/${id}/cancel`
    );
    return mapApiPaymentIntentToPaymentIntent(apiIntent);
  }

  async processRefund(refund: ProcessRefundDTO): Promise<Refund> {
    const client = getApiClient();
    const request = {
      paymentIntentId: refund.paymentIntentId,
      gateway: refund.gateway,
      amount: refund.amount,
      reason: refund.reason,
    };
    const apiRefund = await client.post<ApiRefundResponse>('v1/payments/gateway/refund', request);
    return mapApiRefundToRefund(apiRefund);
  }
}

